import React, { useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import PageLayout from '@/components/PageLayout';
import { useFluid } from '@/contexts/FluidContext';
import { useAuth } from '@/contexts/AuthProvider';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useSettings } from '@/contexts/SettingsContext';
import { uploadCarImage } from '@/lib/uploadCarImage';
import { X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useI18n } from '@/i18n/LanguageContext';

type FormData = {
  fluid_type: 'engine_oil' | 'brake_fluid' | 'coolant' | 'washer_fluid' | 'other';
  other_type?: string;
  spec_code?: string;
  color?: string;
  action_type: 'refill' | 'replacement';
  action_date: string;
  odometer_reading?: string;
  notes?: string;
};

const AddFluidPage = () => {
  const { carId } = useParams<{ carId: string }>();
  const { addFluid } = useFluid();
  const { user } = useAuth();
  const { toast } = useToast();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const { t } = useI18n();

  const formSchema = useMemo(() => z.object({
    fluid_type: z.enum(['engine_oil', 'brake_fluid', 'coolant', 'washer_fluid', 'other']),
    other_type: z.string().optional(),
    spec_code: z.string().optional(),
    color: z.string().optional(),
    action_type: z.enum(['refill', 'replacement']),
    action_date: z.string().min(1, t('addFluid.validationActionDateRequired')),
    odometer_reading: z.string().optional(),
    notes: z.string().optional(),
  }), [t]);
  
  // Image upload state
  const [imageUploading, setImageUploading] = useState(false);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [session, setSession] = useState(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fluid_type: 'engine_oil',
      action_type: 'refill',
      action_date: new Date().toISOString().split('T')[0],
      odometer_reading: '',
    },
  });

  const watchedFluidType = form.watch('fluid_type');

  // Get session for image upload
  React.useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        setSession(data.session);
      }
    });
  }, []);

  const handleAddImageClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleImageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const previewUrl = URL.createObjectURL(file);
    setLocalPreview(previewUrl);
    setSelectedFile(file);
  };

  const handleRemoveImage = () => {
    if (localPreview) {
      URL.revokeObjectURL(localPreview);
      setLocalPreview(null);
    }
    setSelectedFile(null);
  };

  const onSubmit = async (data: FormData) => {
    if (!user || !carId) return;

    try {
      let mediaPath = undefined;

      // Upload image if selected
      if (selectedFile && user && session) {
        setImageUploading(true);
        try {
          mediaPath = await uploadCarImage({
            file: selectedFile,
            userId: user.id,
            entityId: carId,
            type: 'fluid',
            accessToken: session.access_token,
          });
        } catch (uploadError) {
          console.error('uploadCarImage error:', uploadError);
          throw uploadError;
        }
      }

      // Create fluid first
      const fluidPayload = {
        car_id: carId,
        fluid_type: data.fluid_type,
        other_type: data.fluid_type === 'other' ? data.other_type : undefined,
        spec_code: data.spec_code || undefined,
        color: data.fluid_type === 'coolant' ? data.color : undefined,
        action_type: data.action_type,
        action_date: new Date(data.action_date).toISOString(),
        notes: data.notes || undefined,
        user_id: user.id,
        media_path: mediaPath,
      } as const;

      await addFluid(fluidPayload as any);

      // Optionally insert odometer reading linked to this fluid action
      if (data.odometer_reading) {
        const parsed = parseInt(data.odometer_reading, 10);
        if (!Number.isNaN(parsed)) {
          const readingKm = settings?.distanceUnit === 'mi' ? Math.round(parsed * 1.609344) : parsed;
          // find the latest fluid we just inserted by timestamp
          const { data: latestFluid } = await supabase
            .from('fluids')
            .select('id')
            .eq('car_id', carId)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          if (latestFluid?.id) {
            await supabase.from('odometer_readings').insert({
              car_id: carId,
              entered_by: user.id,
              reading: readingKm,
              source: 'fluid_log',
              related_id: latestFluid.id,
            });
          }
        }
      }

      toast({
        title: t('addFluid.toastSuccessTitle'),
        description: t('addFluid.toastSuccessDesc'),
      });

      navigate(`/cars/${carId}`);
    } catch (error) {
      toast({
        title: t('addFluid.toastErrorTitle'),
        description: t('addFluid.toastErrorDesc'),
        variant: 'destructive',
      });
    } finally {
      setImageUploading(false);
    }
  };

  return (
    <PageLayout 
      title={t('addFluid.title')} 
      showBackButton 
      onBack={() => navigate(`/cars/${carId}`)}
    >
      <div className="max-w-md mx-auto space-y-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="fluid_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('addFluid.fluidType')}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('addFluid.selectFluidType')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="engine_oil">{t('fluids.engineOil')}</SelectItem>
                      <SelectItem value="brake_fluid">{t('fluids.brakeFluid')}</SelectItem>
                      <SelectItem value="coolant">{t('carDetail.fields.coolantAntifreeze')}</SelectItem>
                      <SelectItem value="washer_fluid">{t('carDetail.fields.windshieldFluid')}</SelectItem>
                      <SelectItem value="other">{t('addFluid.other')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {watchedFluidType === 'other' && (
              <FormField
                control={form.control}
                name="other_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('addFluid.specifyType')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('addFluid.otherTypePlaceholder')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="spec_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('addFluid.specCode')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('addFluid.specCodePlaceholder')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {watchedFluidType === 'coolant' && (
              <FormField
                control={form.control}
                name="color"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('addFluid.color')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('addFluid.colorPlaceholder')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="action_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('addFluid.actionType')}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('addFluid.selectActionType')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="refill">{t('addFluid.refill')}</SelectItem>
                      <SelectItem value="replacement">{t('addFluid.replacement')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="action_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('addFluid.actionDate')}</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="odometer_reading"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('addFluid.odometerReading')}</FormLabel>
                  <FormControl>
                    <Input type="number" inputMode="numeric" placeholder={t('addFluid.odometerPlaceholder')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Image Upload Section */}
            <div className="space-y-2">
              <Label
                style={{
                  color: '#333333',
                  fontSize: 14,
                  fontFamily: 'Lato',
                  fontWeight: 400,
                  lineHeight: '20px',
                  letterSpacing: 0.10,
                  wordWrap: 'break-word'
                }}
              >
                {t('addFluid.labelPhotoHint')}
              </Label>
              <div style={{ display: 'flex', justifyContent: 'center', width: '100%', gap: 16, marginBottom: 0 }}>
                {localPreview ? (
                  <div style={{ position: 'relative', width: 100, height: 76, borderRadius: 10.18, overflow: 'hidden', boxShadow: '0px 10px 8px -1px #EEEEEE', background: '#FDFDFD', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    <img
                      src={localPreview}
                      alt={t('addFluid.labelPreviewAlt')}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    >
                      <X size={14} color="#fff" />
                    </button>
                  </div>
                ) : (
                  <div
                    style={{width: 100, height: 76, paddingLeft: 34, paddingRight: 34, paddingTop: 22, paddingBottom: 22, background: '#FDFDFD', borderRadius: 10.18, justifyContent: 'flex-start', alignItems: 'center', gap: 10, display: 'inline-flex', boxShadow: '0px 10px 8px -1px #EEEEEE', cursor: 'pointer'}}
                    onClick={handleAddImageClick}
                  >
                    <div style={{width: 32, height: 32, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      <img src="/icons/AddCarImage.png" alt={t('addFluid.addImageAlt')} style={{width: 28, height: 28, objectFit: 'contain'}} />
                    </div>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleImageInputChange}
                  disabled={imageUploading}
                />
              </div>
              {imageUploading && (
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <LoadingSpinner />
                  <span>{t('addFluid.uploadingImage')}</span>
                </div>
              )}
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('addFluid.notes')}</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder={t('addFluid.notesPlaceholder')}
                      className="resize-none"
                      rows={3}
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/cars/${carId}`)}
                className="flex-1"
              >
                {t('addFluid.cancel')}
              </Button>
              <Button type="submit" className="flex-1">
                {t('addFluid.submit')}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </PageLayout>
  );
};

export default AddFluidPage;


